// @ts-nocheck

import { isEmpty, mergeDeepRight } from 'ramda';

import { di } from '@ezugi/constants';
import { type GameSounds } from '@ezugi/types';
import { type Skin, setTheme } from '@ezugi/ui/setTheme';
import { DeviceInfo } from '@ezugi/utils/src/helpers/deviceInfo';

import {
  actions,
  createStore,
  StoreOverrideProps,
} from './store';
import { configActions } from './store/actions';
import { payoutsEpic } from './store/epics/config';
import { LoadTableConfigResponse } from './store/epics/config/fetch';
import { type BOConfig } from './store/reducers/config';
import { languagesSelector } from './store/selectors';
import {
  addEpic$,
  addReducer$,
  state$,
  stateSubject,
} from './streams';
import { handleEvoWrapperMessages } from './util/handleEvoWrapperMessages';
import { setViewportSizeVariables } from './util/viewport';

import type { RootState, GameLoadedRootState } from './store/types';

export { actions, constants, reducers, selectors } from './store';

export { BOConfig, RootState, GameLoadedRootState };

export { state$ };

export type GameOptions = {
  localeGroups?: string[];
  prefetchTableConfig?: LoadTableConfigResponse;
  /** array of web socket `MessageType`s received by client from server which won't be delayed by video delay (will be processed instantly) */
  promptStates?: string[];
  fetchPayouts?: boolean;
  store?: StoreOverrideProps;
  sounds?: GameSounds;
  retailMode?: boolean;
  skin?: Skin;
};

function getScaleRatio() {
  const { innerWidth, innerHeight } = window;
  const r = Math.min(innerWidth / 1440, innerHeight / 810);
  window.scaleRatio = r;
  return r;
}

function scaleUI(app: any) {
  app.style.transform = `scale(${getScaleRatio()})`;
}

const addAppListeners = () => {
  const app = document.getElementById('app');
  let timeout;

  const handlePageShow = (event) => {
    const shouldReload = event.persisted
      || performance.getEntriesByType('navigation').at(-1)?.type
        === 'back_forward';

    if (shouldReload) {
      window.location.reload();
    }
  };

  const handlerForEvoWrapper = (event) => handleEvoWrapperMessages(event, state$);

  const handleDesktopResize = () => {
    if (timeout) {
      window.cancelAnimationFrame(timeout);
    }

    timeout = window.requestAnimationFrame(() => scaleUI(app));
  };

  const handleMobileResize = () => setViewportSizeVariables();

  window.addEventListener('pageshow', handlePageShow);

  window.TOGGLE_LOCALIZATION = () => {
    const {
      localeActions: { locale },
    } = actions;
    state$.dispatch(locale.toggle());
  };

  window.CHANGE_LOCALIZATION = (code?: string) => {
    const allLanguages = languagesSelector(state$.source.getValue());

    if (!code || !allLanguages.some((el) => el.code === code)) {
      // eslint-disable-next-line no-console
      console.log('Please pass lang code from following codes:', allLanguages.map((el) => el.code));
      return;
    }

    const {
      localeActions: { locale },
    } = actions;
    state$.dispatch(locale.change({ code }));
  };

  window.LOCALIZATION_PREVIEW = (time = 5000) => {
    const allLanguages = languagesSelector(state$.source.getValue());

    const {
      localeActions: { locale },
    } = actions;

    allLanguages.forEach((lang, i) => {
      setTimeout(() => {
        // eslint-disable-next-line no-console
        console.log('Switched lang to ', lang.code, lang.label);

        state$.dispatch(locale.change({ code: lang.code }));
        if (i === allLanguages.length - 1) {
          // eslint-disable-next-line no-console
          console.log('LOCALIZATION_PREVIEW completed');
        }
      }, time * i);
    });
  };

  window.addEventListener('message', handlerForEvoWrapper);

  if (DeviceInfo.isIOS) {
    document.body.classList.toggle(di.ios);
  }

  if (!DeviceInfo.isHandheld) {
    document.body.classList.toggle(di.desktop);
    app.classList.toggle('desktop-app');
    window.addEventListener(
      'resize',
      handleDesktopResize,
      false
    );

    scaleUI(app);
  } else {
    handleMobileResize();

    window.addEventListener('resize', handleMobileResize);
  }

  return () => {
    window.removeEventListener('message', handlerForEvoWrapper);
    window.removeEventListener('pageshow', handlePageShow);

    if (timeout) {
      window.cancelAnimationFrame(timeout);
    }

    app.classList.remove(...app.classList);
    document.body.classList.remove(...document.body.classList);

    window.removeEventListener(
      'resize',
      handleDesktopResize,
      false
    );

    window.removeEventListener('resize', handleMobileResize);

    app.attributeStyleMap.clear();
  };
};

export const createGame = (_config: GameOptions) => {
  const {
    localeGroups = [],
    store: storeOptions,
    sounds = {},
    retailMode = false,
    promptStates = [],
    fetchPayouts,
    prefetchTableConfig,
  } = _config;

  const {
    reducers,
    epics,
    overridableEpics,
    handleOperatorError,
    handleError,
    preloadedState = {},
  } = storeOptions;

  if (_config.skin && !isEmpty(_config.skin)) {
    setTheme(_config.skin);
  }

  const { store, addEpic, addReducer } = createStore({
    reducers,
    handleOperatorError,
    handleError,
    epics: fetchPayouts ? [ ...epics, payoutsEpic ] : epics,
    overridableEpics,
    preloadedState: mergeDeepRight(
      {
        app: { sounds, retailMode, promptStates },
      },
      preloadedState
    ),
  });

  addEpic$.subscribe(addEpic);

  addReducer$.subscribe(addReducer);

  store.dispatch(configActions.config.request({ config: prefetchTableConfig, localeGroups }));

  Object.defineProperty(state$, 'dispatch', {
    writable: false,
    enumerable: false,
    value: store.dispatch.bind(store),
    configurable: true,
  });

  store.subscribe(() => {
    stateSubject.next(store.getState());
  });

  const cleanup = addAppListeners();

  return { store, cleanup };
};
